import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, setRemoteSession, ApiError } from '../api';

// Public page where a remote iPad claims a 6-digit pairing code (typed or
// pre-filled from a QR's ?code=). On success it stores the scoped session
// token and routes straight into the right console — no login.

const CODE_ERRORS: Record<string, string> = {
  invalid_code: "That code isn't valid. Double-check the 6 digits.",
  code_expired: 'That code has expired. Ask for a fresh one.',
  game_over: 'That game has ended.',
};

export function JoinPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Auto-claim if the QR brought a ?code=.
  useEffect(() => {
    const qp = new URLSearchParams(window.location.search).get('code');
    if (qp && /^\d{6}$/.test(qp)) {
      setCode(qp);
      void claim(qp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function claim(value: string) {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.claimRemoteCode(value);
      setRemoteSession({
        token: res.accessToken,
        gameId: res.gameId,
        role: res.role,
        game: res.game,
      });
      navigate(
        res.role === 'SCORER'
          ? `/games/${res.gameId}`
          : `/games/${res.gameId}/stats`,
        { replace: true },
      );
    } catch (e) {
      const code =
        e instanceof ApiError ? (e.body as { message?: string })?.message : '';
      setErr(CODE_ERRORS[code ?? ''] ?? 'Could not join with that code.');
    } finally {
      setBusy(false);
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (/^\d{6}$/.test(code)) void claim(code);
    else setErr('Enter the 6-digit code.');
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5 text-center">
        <div>
          <div className="text-2xl font-extrabold tracking-tight">
            Score<span className="text-brand">Lync</span>
          </div>
          <div className="mt-1 text-sm text-muted">
            Enter the code from the game screen to connect this device.
          </div>
        </div>
        <input
          inputMode="numeric"
          autoFocus
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          className="w-full rounded-xl border-2 border-border bg-surface px-4 py-4 text-center font-mono text-4xl tracking-[0.5em] outline-none focus:border-brand"
        />
        {err && <p className="text-sm text-red-300">{err}</p>}
        <button
          type="submit"
          disabled={busy || code.length !== 6}
          className="btn btn-primary w-full py-3 text-base"
        >
          {busy ? 'Connecting…' : 'Connect'}
        </button>
        <p className="text-xs text-muted">
          No login needed. This device will only control the one game it's
          paired to.
        </p>
      </form>
    </div>
  );
}
